import { showInfoModal } from "./modal";
import { fetch } from "@/utilities";
import { html, nothing, TemplateResult } from "lit";
import { until } from "lit/directives/until.js";
import { I18n } from "@/i18n/i18n";

const MAX_LINES = 99;

export function showInlineFile(name: string, content: string): void {
    showInfoModal(name, html`<div class="code">${content}</div>`);
}

/**
 * Shows the file at `url` in the info modal. The modal shows a loading message until the file has
 * loaded. A file that fails to load shows an error message and no download link.
 */
export function showRealFile(name: string, url: string): void {
    const lines = loadLines(url);
    const download = html`<a
        href="${url}"
        class="btn btn-icon btn-icon-muted"
        title="${I18n.t("js.download")}"
        aria-label="${I18n.t("js.download")}"
        download
    >
        <i class="mdi mdi-download" aria-hidden="true"></i>
    </a>`;

    showInfoModal(
        name,
        html`${until(lines.then(renderLines), html`<div class="code">${I18n.t("js.loading")}</div>`)}`,
        html`${until(
            lines.then((loaded) => (loaded ? download : nothing)),
            download,
        )}`,
    );
}

// Resolves to null if the file could not be loaded.
async function loadLines(url: string): Promise<string[] | null> {
    let data: string;
    try {
        const response = await fetch(url, { method: "GET" });
        data = await response.text();
    } catch {
        return null;
    }

    const lines = data.split("\n");
    if (lines.length > MAX_LINES) {
        return [...lines.slice(0, MAX_LINES), "..."];
    }
    return lines;
}

function renderLines(lines: string[] | null): TemplateResult {
    if (!lines) {
        return html`<div class="alert alert-danger mb-0" role="alert">${I18n.t("js.file_viewer.load_failed")}</div>`;
    }

    const table = html`<table class="external-file">
        ${lines.map(
            (line, i) =>
                html`<tr>
                    <td class="line-nr">${i === MAX_LINES ? "" : i + 1}</td>
                    <td class="line">${line}</td>
                </tr>`,
        )}
    </table>`;
    // `.code` keeps white space, so the table is its only child.
    return html`<div class="code">${table}</div>`;
}
